import type { ExceptionDetail } from "@generated/types";

/**
 * PII-safe Sentry signals distilled from a caught recipient-creation failure.
 *
 * Split into the two shapes `reportSilentFailure` takes: `tags` are the
 * low-cardinality values worth filtering an issue list by, `extra` carries the
 * correlation id used to pivot to the BFF's Kibana logs.
 */
export interface BeneficiaryFailureSignals {
  tags: Record<string, string>;
  extra: Record<string, string | number | boolean>;
}

interface BeneficiaryFailureContext {
  /** Whether the request carried a CoP `verification` block. */
  hasCopVerification?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * `status` is present on every `FetchBaseQueryError` — a number for HTTP
 * responses, one of the transport discriminators otherwise. Kept as a string
 * so `FETCH_ERROR` and `502` are filterable through the same tag.
 */
function readStatus(error: unknown): string {
  if (!isRecord(error)) return "unknown";
  if (error instanceof Error) return "none";
  const { status } = error;
  if (typeof status === "number") return String(status);
  if (typeof status === "string") return status;
  return "unknown";
}

/**
 * The structured BFF error body, when the failure carried one.
 *
 * The `isRecord(error.data)` guard is load-bearing, not incidental: on a
 * `PARSING_ERROR` RTK puts the RAW RESPONSE BODY on `.data` as a string, and
 * the SyntaxError text — which echoes a slice of that body around the bad
 * token — on `.error`. Neither may be forwarded. Returning null for a
 * non-object `.data` is what keeps both out; the `originalStatus` below is the
 * PII-free part worth keeping.
 */
function readExceptionDetail(error: unknown): Partial<ExceptionDetail> | null {
  if (!isRecord(error) || !isRecord(error.data)) return null;
  return error.data as Partial<ExceptionDetail>;
}

/**
 * The error's constructor/class name, for the two shapes that carry one.
 *
 * `unwrap()` rejects with a `FetchBaseQueryError` **or** a `SerializedError` —
 * the latter a plain object `{name, message, stack, code}` produced by RTK's
 * `miniSerializeError`, NOT an `Error` instance. Gating on `instanceof Error`
 * alone throws away the one identifying field RTK preserved for that class.
 */
function readErrorName(error: unknown): string | undefined {
  if (error instanceof Error) return error.name;
  if (isRecord(error) && typeof error.name === "string") return error.name;
  return undefined;
}

/**
 * Reduce a caught value to fixed-vocabulary facts about the failure.
 *
 * Deliberately narrow. `reportSilentFailure` tags and extras are NOT scrubbed
 * by `sharedBeforeSend`, and the upstream `ExceptionDetail` carries
 * `message` / `messages` / `parameters` that echo what the user typed —
 * CUSTOMER-UI-3Q put a customer's BIC in exactly those fields. So only the
 * service-owned identifiers cross over: the HTTP status, the exception `type`,
 * the originating service, and the OTel `spanId` that correlates the event to
 * the BFF's own logs. Nothing derived from the request or response body.
 */
export function describeBeneficiaryFailure(
  error: unknown,
  context: BeneficiaryFailureContext = {}
): BeneficiaryFailureSignals {
  const tags: Record<string, string> = { httpStatus: readStatus(error) };
  const extra: Record<string, string | number | boolean> = {};

  const detail = readExceptionDetail(error);
  if (typeof detail?.type === "string") tags.exceptionType = detail.type;
  if (typeof detail?.origin === "string") tags.origin = detail.origin;
  if (typeof detail?.spanId === "string") extra.spanId = detail.spanId;

  // On a PARSING_ERROR the only thing that distinguishes a gateway 5xx from a
  // malformed 200 is `originalStatus` — and it is the discriminator the rest
  // of the codebase treats as essential (CUSTOMER-UI-6Y: counting a malformed
  // 200 as an outage inflates the upstream-availability report). It is a
  // number, so it carries no body bytes.
  if (isRecord(error) && typeof error.originalStatus === "number") {
    tags.originalStatus = String(error.originalStatus);
  }

  const errorName = readErrorName(error);
  if (errorName) tags.errorName = errorName;

  if (context.hasCopVerification !== undefined) {
    tags.hasCopVerification = String(context.hasCopVerification);
  }

  return { tags, extra };
}
